import { memo } from 'react';
import { useStore } from 'zustand';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { AccountOverride, ImportReviewStoreApi } from '../../lib/state/importReviewStore';

type AccountProposal = {
  accountNumber: string;
  existingAccountId: string | null;
  suggestedName: string | null;
  transactionCount: number;
};

type Member = { id: string; displayName: string };

interface AccountProposalCardProps {
  store: ImportReviewStoreApi;
  members: Member[];
}

export const AccountProposalCard = memo(function AccountProposalCard({
  store,
  members,
}: AccountProposalCardProps) {
  const proposals = useStore(store, (state) => state.accountProposals);

  if (proposals.length === 0) return null;

  return (
    <section className="overflow-hidden rounded-2xl border border-border bg-card">
      <header className="px-4 pt-4 pb-3 sm:px-5">
        <h2 className="text-[15px] font-semibold leading-6">Bankkonton i filen</h2>
        <p className="text-sm text-muted-foreground">
          Godkänn konton som ska kopplas till importerade transaktioner.
        </p>
      </header>
      <div className="divide-y divide-border border-t border-border">
        {proposals.map((proposal) => (
          <AccountProposalRow
            key={proposal.accountNumber}
            store={store}
            proposal={proposal}
            members={members}
          />
        ))}
      </div>
    </section>
  );
});

interface AccountProposalRowProps {
  store: ImportReviewStoreApi;
  proposal: AccountProposal;
  members: Member[];
}

const AccountProposalRow = memo(function AccountProposalRow({
  store,
  proposal,
  members,
}: AccountProposalRowProps) {
  const override = useStore(store, (state) => state.accountOverrides[proposal.accountNumber]);
  const setAccountOverride = useStore(store, (state) => state.setAccountOverride);

  if (!override) {
    return null;
  }

  const isExisting = !!proposal.existingAccountId;

  const updateOverride = (nextOverride: AccountOverride) => {
    setAccountOverride(proposal.accountNumber, nextOverride);
  };

  return (
    <div
      className={cn(
        'flex items-start gap-4 px-4 py-3 sm:items-center sm:px-5',
        !override.approve && 'opacity-50',
      )}
    >
      <Checkbox
        aria-label={`Godkänn konto ${proposal.accountNumber}`}
        className="mt-0.5 after:-inset-3.5 sm:mt-0 sm:after:-inset-x-3 sm:after:-inset-y-2"
        checked={override.approve}
        disabled={isExisting}
        onCheckedChange={(checked) =>
          updateOverride({
            ...override,
            approve: checked === true,
          })
        }
      />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-mono text-sm text-foreground">{proposal.accountNumber}</span>
          <Badge
            variant="outline"
            className={
              isExisting
                ? 'text-muted-foreground'
                : 'border-transparent bg-positive-soft text-positive'
            }
          >
            {isExisting ? 'befintligt' : 'nytt'}
          </Badge>
          <span className="text-xs text-muted-foreground tabular-nums">
            {proposal.transactionCount} transaktioner
          </span>
        </div>
        {!isExisting && override.approve && (
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              type="text"
              value={override.friendlyName}
              onChange={(event) =>
                updateOverride({
                  ...override,
                  friendlyName: event.target.value,
                })
              }
              placeholder="Namn (valfritt)"
              aria-label={`Namn för konto ${proposal.accountNumber}`}
              className="h-11 w-full text-base sm:h-8 sm:w-40 sm:text-xs"
            />
            <Select
              value={override.memberId || '__none__'}
              onValueChange={(value) =>
                updateOverride({
                  ...override,
                  memberId: value === '__none__' ? '' : value,
                })
              }
            >
              <SelectTrigger
                aria-label={`Ägare för konto ${proposal.accountNumber}`}
                className="w-full data-[size=default]:h-11 sm:w-40 sm:text-xs sm:data-[size=default]:h-8"
              >
                <SelectValue placeholder="Ingen ägare" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Ingen ägare</SelectItem>
                {members.map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {member.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
});
